import axios, { AxiosResponse } from 'axios';
import {
  ConfigurationApiListItem,
  ConfigurationApiDetails,
  apiTypeId,
} from '../../../components/SystemSettings/ConfigurationApi/configurationApi.types';
import {
  ISpecialCaseTitles,
  SpecialCase,
  SpecialCaseFilter,
  SpecialCaseResult,
  SpecialCasesSortBy,
} from '../../../components/SystemSettings/SpecialCases/SpecialCases.types';
import { FlagStatus, FlagType } from '../../../components/SystemSettings/SystemFlags/Subcomponents/Flag.types';

import EnvWindow from '../../../utils/env';
import { addParam } from '../../../utils/servicesHelper';
import { IConfigurationKsdo } from '../../../components/SystemSettings/ConfigurationKsdo/ConfigurationKsdo.types';
import { ConfigurationOkeDetails } from '../../../components/SystemSettings/ConfigurationOke/ConfigurationOke.types';
import { SmsNotificationSetupItem, ModifiedSmsNotificationItem } from '../../../components/SystemSettings/SmsNotificationSetupManager/SmsNotificationSetupManager.component';
import { LabelsListItem, ViewWithLabelsDto } from '../../../components/SystemSettings/LabelManager/Label.types';

const defaultFilter = {
  id: '',
  title: '',
  description: '',
  recipients: [],
  active: [true],
};

export async function getSmsNotificationSetups(): Promise<SmsNotificationSetupItem[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN_API_URL}/SmsNotificationSetup`);
  return response.data;
}

export async function updateSmsNotificationSetups(smsNotificationSetups: ModifiedSmsNotificationItem[]):
  Promise<boolean[]> {
  const response = await axios.patch(`${EnvWindow.REACT_APP_ADMIN_API_URL}/SmsNotificationSetup`, smsNotificationSetups);
  return response.data;
}

export async function getFlags(): Promise<FlagType[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN__FLAGS_API_URL}`);
  return response.data;
}

export async function updateFlags(flags: FlagStatus[]): Promise<AxiosResponse<{ success: boolean }>> {
  return await axios.patch(`${EnvWindow.REACT_APP_ADMIN__FLAGS_API_URL}`, flags);
}

export async function getConfigurationApi(value?: boolean): Promise<ConfigurationApiListItem[]> {
  let params = '';
  params += addParam('okeApi', value ? value : false);
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN__CONFIGURATION_API_API_URL}?${params}`);
  return response.data;
}

export async function getConfigurationApiDetails(configurationApiId: string): Promise<ConfigurationApiDetails> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN__CONFIGURATION_API_API_URL}/${configurationApiId}`);
  return response.data;
}

export async function getConfigurationApiDetailsOke(configurationApiId: string): Promise<ConfigurationOkeDetails> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN__CONFIGURATION_API_API_URL}/${configurationApiId}`);
  return response.data;
}

export async function updateConfigurationApiDetails(
  configurationApiId: string,
  name: string,
  description: string,
  servicesHost: string,
  secretNameFirstValue: string,
  secretNameSecondValue: string,
  configurationApiTypeId: apiTypeId,
): Promise<ConfigurationApiDetails> {
  const response = await axios.put(`${EnvWindow.REACT_APP_ADMIN__CONFIGURATION_API_API_URL}/${configurationApiId}`, {
    name: name,
    description: description,
    servicesHost: servicesHost,
    secretNameFirstValue: secretNameFirstValue,
    secretNameSecondValue: secretNameSecondValue,
    configurationApiTypeId: configurationApiTypeId,
  });
  return response.data;
}

export async function updateConfigurationApiDetailsOke(
  configurationApiId: string,
  name: string,
  description: string,
  servicesHost: string,
  secretNameFirstValue: string,
  secretNameSecondValue: string,
  configurationApiTypeId: apiTypeId,
): Promise<ConfigurationOkeDetails> {
  const response = await axios.put(`${EnvWindow.REACT_APP_ADMIN__CONFIGURATION_API_API_URL}/${configurationApiId}`, {
    name: name,
    description: description,
    servicesHost: servicesHost,
    secretNameFirstValue: secretNameFirstValue,
    secretNameSecondValue: secretNameSecondValue,
    configurationApiTypeId: configurationApiTypeId,
  });
  return response.data;
}

export async function getSpecialCasesDefinitions(
  filter: SpecialCaseFilter = defaultFilter,
  sortByColumnId: SpecialCasesSortBy = 'title',
  isDescending = true,
  signal?: AbortSignal,
): Promise<SpecialCase[]> {
  let params = '';
  params += filter.recipients.includes('domesticCoordinator') ? addParam('domesticCoordinator', true) : '';
  params += filter.recipients.includes('examTaskGroupCoordinator') ? addParam('examTaskGroupCoordinator', true) : '';
  params += filter.recipients.includes('examiningTeamChairman') ? addParam('examiningTeamChairman', true) : '';
  params += filter.recipients.includes('techAdmin') ? addParam('techAdmin', true) : '';
  params += addParam('title', filter.title ? filter.title : '');
  params += addParam('description', filter.description ? filter.description : '');
  params += addParam('sortBy', sortByColumnId);
  params += addParam('descending', isDescending ? 'true' : 'false');

  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN__SPECIAL_CASE_DEFINITION_API_URL}?${params}`, {
    signal: signal ?? undefined,
  });
  return response.data;
}

export async function getSpecialCasesDefinitionsTitles(): Promise<ISpecialCaseTitles[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN__SPECIAL_CASE_DEFINITION_API_URL}/titles`);
  return response.data;
}

export async function createSpecialCasesDefinition(
  title: string,
  description: string,
  domesticCoordinator: boolean,
  examTaskGroupCoordinator: boolean,
  examiningTeamChairman: boolean,
  techAdmin: boolean,
): Promise<AxiosResponse<SpecialCaseResult>> {
  const response = await axios.post(`${EnvWindow.REACT_APP_ADMIN__SPECIAL_CASE_DEFINITION_API_URL}`, {
    title: title,
    description: description,
    domesticCoordinator: domesticCoordinator,
    examTaskGroupCoordinator: examTaskGroupCoordinator,
    examiningTeamChairman: examiningTeamChairman,
    techAdmin: techAdmin,
  });
  return response.data;
}

export async function updateSpecialCasesDefinition(
  id: string,
  title: string,
  description: string,
  domesticCoordinator: boolean,
  examTaskGroupCoordinator: boolean,
  examiningTeamChairman: boolean,
  techAdmin: boolean,
): Promise<AxiosResponse<SpecialCaseResult>> {
  const response = await axios.patch(`${EnvWindow.REACT_APP_ADMIN__SPECIAL_CASE_DEFINITION_API_URL}/${id}`, {
    title: title,
    description: description,
    domesticCoordinator: domesticCoordinator,
    examTaskGroupCoordinator: examTaskGroupCoordinator,
    examiningTeamChairman: examiningTeamChairman,
    techAdmin: techAdmin,
  });
  return response.data;
}

export async function deleteSpecialCasesDefinition(id: string): Promise<AxiosResponse<SpecialCaseResult>> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_ADMIN__SPECIAL_CASE_DEFINITION_API_URL}/${id}`);
  return response.data;
}

export async function getIdleTime(): Promise<number | string> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN_API_URL}/UserSession/idleTime`);
  return response.data;
}

export async function setIdleTime(idleTime: number): Promise<number> {
  const response = await axios.patch(`${EnvWindow.REACT_APP_ADMIN_API_URL}/UserSession/idleTime`, { idleTime });
  return response.data;
}

export async function getKsdoConfiguration(): Promise<IConfigurationKsdo> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN__CONFIGURATION_KSDO_API_URL}`);
  return response.data;
}

export async function updateKsdoConfiguration(
  isKsdoLoginEnabled: boolean,
  clientId: string,
  clientSecret: string,
  url: string,
): Promise<IConfigurationKsdo> {
  const response = await axios.put(`${EnvWindow.REACT_APP_ADMIN__CONFIGURATION_KSDO_API_URL}`, {
    isKsdoLoginEnabled: isKsdoLoginEnabled,
    clientId: clientId,
    clientSecret: clientSecret,
    url: url,
  });
  return response.data;
}

export async function getLabels(): Promise<ViewWithLabelsDto[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN__LABELS_URL}`);
  return response.data;
}

export async function updateLabel(viewId: string, content: LabelsListItem[]) {
  const response = await axios.patch(`${EnvWindow.REACT_APP_ADMIN__LABELS_URL}/${viewId}`, content)
  return response.data;
}


